import { useState } from 'react'
import { useCreateNegativeProductTarget } from '@/api/targets'
import { buildAsinExpression, buildBrandExpression, ASIN_RE } from '@/api/targetExpression'
import { useCampaigns, useAdGroups } from '@/api/campaigns'
import { IS_DEMO_MODE } from '@/lib/demoMode'
import { isReconcileRequiredError } from '@/api/client'
import { X, AlertCircle, AlertTriangle } from 'lucide-react'

type Mode = 'asin' | 'brand'
type Level = 'campaign' | 'adgroup'

export default function NegativeTargetDrawer({
  open,
  onClose,
}: {
  open: boolean
  onClose: () => void
}) {
  const [mode, setMode] = useState<Mode>('asin')
  const [level, setLevel] = useState<Level>('adgroup')
  const [campaignId, setCampaignId] = useState<number | ''>('')
  const [adGroupId, setAdGroupId] = useState<number | ''>('')
  const [asinText, setAsinText] = useState('')
  const [brandId, setBrandId] = useState('')
  const [error, setError] = useState<string | null>(null)
  // Scheibe S (REST-O): getrennt vom gewoehnlichen `error` — siehe TargetDetailDrawer.
  const [reconcileHinweis, setReconcileHinweis] = useState<string | null>(null)
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null)
  const create = useCreateNegativeProductTarget()
  const campaigns = useCampaigns({ limit: 200, sortBy: 'updated_at', sortDir: 'desc' })
  const adGroups = useAdGroups(campaignId === '' ? null : Number(campaignId), { limit: 200 })

  if (!open) return null

  function buildExpressions(): string[] | null {
    if (mode === 'asin') {
      const asins = asinText.split(/\s+/).filter(Boolean)
      const invalid = asins.filter((a) => !ASIN_RE.test(a))
      if (invalid.length > 0) {
        setError(`Ungueltige ASINs: ${invalid.slice(0, 3).join(', ')}`)
        return null
      }
      if (asins.length === 0) {
        setError('Mindestens eine ASIN erforderlich')
        return null
      }
      // Pro ASIN ein eigener POST
      return asins.map((a) => buildAsinExpression([a]))
    }
    if (!brandId.trim()) {
      setError('Brand-ID erforderlich')
      return null
    }
    return [buildBrandExpression(brandId.trim())]
  }

  async function handleSubmit() {
    setError(null)
    setReconcileHinweis(null)
    if (level === 'campaign' && !campaignId) {
      setError('Kampagne wählen')
      return
    }
    if (level === 'adgroup' && !adGroupId) {
      setError('Ad Group wählen')
      return
    }

    const expressions = buildExpressions()
    if (!expressions) return

    setProgress({ done: 0, total: expressions.length })
    const results = await Promise.allSettled(
      expressions.map((expression) =>
        create.mutateAsync({
          campaignId: level === 'campaign' && campaignId !== '' ? Number(campaignId) : undefined,
          adGroupId: level === 'adgroup' && adGroupId !== '' ? Number(adGroupId) : undefined,
          expression,
        }),
      ),
    )

    const failed = results.filter((r) => r.status === 'rejected')
    setProgress(null)

    if (failed.length > 0) {
      // Scheibe S (REST-O): Ein Bündel von POSTs kann GEMISCHT scheitern. Sobald
      // EINER davon in den Reconcile-Zustand geraten ist, ist die Gesamtaussage
      // „nicht einfach wiederholen" — auch wenn die Mehrheit gewoehnlich scheiterte.
      // Deshalb wird ueber ALLE Ablehnungen gesucht und nicht nur die erste gelesen.
      const reconcileFaelle = failed
        .map((r) => (r as PromiseRejectedResult).reason)
        .filter(isReconcileRequiredError)
      if (reconcileFaelle.length > 0) {
        const erster = reconcileFaelle[0]
        setReconcileHinweis(
          `${reconcileFaelle.length}/${expressions.length} mit ungeklärtem Amazon-Zustand — ${
            erster instanceof Error ? erster.message : String(erster)
          }`,
        )
        return
      }
      const firstError = (failed[0] as PromiseRejectedResult).reason
      setError(
        `${failed.length}/${expressions.length} fehlgeschlagen — ${
          firstError instanceof Error ? firstError.message : String(firstError)
        }`,
      )
      return
    }

    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40" onClick={onClose}>
      <div
        className="absolute right-0 top-0 flex h-full w-[480px] flex-col bg-[var(--background)] shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-[var(--border)] px-6 py-4">
          <h2 className="text-lg font-semibold">Neues negatives Target</h2>
          <button onClick={onClose}>
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-auto p-6 space-y-5">
          <div>
            <label className="mb-1 block text-sm font-medium">Geltungsbereich</label>
            <div className="flex gap-2">
              <label className="flex items-center gap-1.5 text-sm">
                <input
                  type="radio"
                  checked={level === 'campaign'}
                  onChange={() => setLevel('campaign')}
                />
                Kampagne
              </label>
              <label className="flex items-center gap-1.5 text-sm">
                <input
                  type="radio"
                  checked={level === 'adgroup'}
                  onChange={() => setLevel('adgroup')}
                />
                Ad Group
              </label>
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium">Kampagne</label>
            <select
              value={campaignId}
              onChange={(e) => {
                setCampaignId(e.target.value ? Number(e.target.value) : '')
                setAdGroupId('')
              }}
              className="h-9 w-full rounded-md border border-[var(--border)] bg-[var(--background)] px-3 text-sm"
            >
              <option value="">— wählen —</option>
              {campaigns.data?.campaigns?.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          {level === 'adgroup' && (
            <div>
              <label className="mb-1 block text-sm font-medium">Ad Group</label>
              <select
                value={adGroupId}
                onChange={(e) => setAdGroupId(e.target.value ? Number(e.target.value) : '')}
                disabled={!campaignId}
                className="h-9 w-full rounded-md border border-[var(--border)] bg-[var(--background)] px-3 text-sm disabled:opacity-50"
              >
                <option value="">— wählen —</option>
                {adGroups.data?.adGroups?.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <div className="mb-2 flex gap-1 rounded-md bg-[var(--accent)] p-1">
              {(['asin', 'brand'] as Mode[]).map((m) => (
                <button
                  key={m}
                  onClick={() => setMode(m)}
                  className={`flex-1 rounded px-3 py-1 text-sm ${
                    mode === m ? 'bg-[var(--background)] font-medium' : 'text-[var(--muted-foreground)]'
                  }`}
                >
                  {m === 'asin' ? 'ASIN-Liste' : 'Marke'}
                </button>
              ))}
            </div>

            {mode === 'asin' && (
              <div>
                <label className="mb-1 block text-sm font-medium">ASINs (eine pro Zeile)</label>
                <textarea
                  value={asinText}
                  onChange={(e) => setAsinText(e.target.value)}
                  rows={6}
                  placeholder={`B0073RSTXI\nB07CWVPDL1`}
                  className="w-full rounded-md border border-[var(--border)] bg-[var(--background)] px-3 py-2 font-mono text-sm"
                />
                <p className="mt-1 text-xs text-[var(--muted-foreground)]">
                  Mehrere ASINs erzeugen mehrere POST-Calls (Promise.allSettled).
                </p>
              </div>
            )}
            {mode === 'brand' && (
              <div>
                <label className="mb-1 block text-sm font-medium">Brand-ID</label>
                <input
                  value={brandId}
                  onChange={(e) => setBrandId(e.target.value)}
                  placeholder="z.B. ID Dinkel"
                  className="h-9 w-full rounded-md border border-[var(--border)] bg-[var(--background)] px-3 text-sm"
                />
              </div>
            )}
          </div>

          {progress && (
            <div className="rounded-md bg-[var(--accent)] px-3 py-2 text-sm">
              Speichere {progress.done}/{progress.total}…
            </div>
          )}

          {reconcileHinweis && (
            <div role="alert" className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-900">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                {`Amazon-Zustand ungeklärt — NICHT erneut anlegen, sonst entsteht dort ein Duplikat. Erst bei Amazon nachsehen. (${reconcileHinweis})`}
              </span>
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
              <AlertCircle className="h-4 w-4" /> {error}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-[var(--border)] px-6 py-4">
          <button
            onClick={onClose}
            className="rounded-md border border-[var(--border)] px-4 py-1.5 text-sm"
          >
            Abbrechen
          </button>
          <button
            onClick={handleSubmit}
            disabled={IS_DEMO_MODE || create.isPending || progress !== null}
            title={IS_DEMO_MODE ? 'Demo-Modus aktiv — Mutation blockiert' : ''}
            className="rounded-md bg-[var(--primary)] px-4 py-1.5 text-sm text-[var(--primary-foreground)] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {progress ? 'Speichere…' : 'Anlegen'}
          </button>
        </div>
      </div>
    </div>
  )
}
